// Infinite loop: the slides are cloned onto both ends of the track. Whenever
// scrolling settles inside a clone set, the track jumps instantly by one set
// width so the visible slides stay the same but we are back in the real set.
class ShopByTeaSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.index = 0;
    this.addClones();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.step(-1), { signal });
    this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.step(1), { signal });

    if ('onscrollend' in window) {
      this.track.addEventListener('scrollend', () => this.onSettle(), { signal });
    } else {
      this.track.addEventListener(
        'scroll',
        () => {
          clearTimeout(this.settleTimer);
          this.settleTimer = setTimeout(() => this.onSettle(), 150);
        },
        { signal }
      );
    }

    // Also fires when the slider first becomes visible, which sets the starting position.
    this.resizeObserver = new ResizeObserver(() => this.goTo(this.index));
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    clearTimeout(this.settleTimer);
  }

  addClones() {
    const makeClones = () =>
      this.slides.map((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        return clone;
      });

    this.track.prepend(...makeClones());
    this.track.append(...makeClones());
  }

  get setWidth() {
    return this.track.children[this.slides.length * 2].offsetLeft - this.slides[0].offsetLeft;
  }

  get slideWidth() {
    return this.slides[0].getBoundingClientRect().width;
  }

  goTo(index) {
    this.track.scrollTo({ left: this.slides[index].offsetLeft, behavior: 'instant' });
  }

  // Moves the track back into the real set without any visible change.
  normalize() {
    const start = this.slides[0].offsetLeft;
    const { setWidth } = this;
    if (!setWidth) return;

    if (this.track.scrollLeft < start - 1) {
      this.track.scrollTo({ left: this.track.scrollLeft + setWidth, behavior: 'instant' });
    } else if (this.track.scrollLeft >= start + setWidth - 1) {
      this.track.scrollTo({ left: this.track.scrollLeft - setWidth, behavior: 'instant' });
    }
  }

  onSettle() {
    this.normalize();
    const { slideWidth } = this;
    if (!slideWidth) return;

    const offset = Math.round((this.track.scrollLeft - this.slides[0].offsetLeft) / slideWidth);
    this.index = Math.min(Math.max(offset, 0), this.slides.length - 1);
  }

  step(direction) {
    this.normalize();
    this.track.scrollBy({ left: direction * this.slideWidth });
  }
}

if (!customElements.get('shop-by-tea-slider')) {
  customElements.define('shop-by-tea-slider', ShopByTeaSlider);
}
